import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import jwtDecode from 'jwt-decode';
import {
  Box,
  MuiBox,
  Button,
  Card,
  Typography,
  SvgIconButton,
  FlexBox,
  Grid,
  MuiTable,
  SvgIcon
} from '@kis-technologies/kis-ui';
import { ActionOverlay } from 'components/ActionOverlay';
import { CreateNewUserOverlay } from 'components/CreateNewUserOverlay';
import { UpdateUserDrawer } from 'components/UpdateUserDrawer';
import { useActions } from 'hooks/useActions';
import { selectors as adminsSelectors, actions as adminsActions, AdminWithActivity } from 'store/admins';
import { selectors as authSelectors } from 'store/auth';
import { selectors as clientsSelectors, Client } from 'store/clients';
import { formatTitle } from 'utils/stringFormat';
import { useStyles } from './styled';

// RETURNS USER FRIENDLY STRING FOR ROLE
const getAdminRole = (role: string) => {
  if (role.toLowerCase().includes('maintenance')) return 'maintenance';
  if (role.toLowerCase().includes('client')) return 'admin';
  if (role.toLowerCase().includes('readonly')) return 'Read-Only Admin';
  if (role.toLowerCase().includes('global')) return 'global admin';
  return '';
};

// RETURNS LATEST ACTIVITY TIME OR USER FRIENDLY STRING IF NULL
const getAdminLatestLog = (admin: AdminWithActivity) => {
  if (admin.latestActivity) return format(new Date(admin.latestActivity.createdAt), 'MMMM dd, yyyy');
  return null;
};

// RETURNS LATEST ACTIVITY EVENT OR USER FRIENDLY STRING IF NULL
const getAdminLatestActivity = (admin: AdminWithActivity) => {
  if (admin.latestActivity) return admin.latestActivity.event;
  return null;
};

// SORTS THE ADMINS BASED ON THE SORT PARAMS (KEY & DIRECTION)
const sortAdmins = (admins: AdminWithActivity[], sortKey: string, sortDirection: 'asc' | 'desc') => {
  return [...admins].sort((a, b) => {
    let aValue: string | number | Date = '';
    let bValue: string | number | Date = '';

    switch (sortKey) {
      case 'type':
        aValue = formatTitle(getAdminRole(a.role));
        bValue = formatTitle(getAdminRole(b.role));
        break;
      case 'name':
        aValue = a.name;
        bValue = b.name;
        break;
      case 'lastLogged':
        const aLatestLog = getAdminLatestLog(a);
        const bLatestLog = getAdminLatestLog(b);
        aValue = aLatestLog ? new Date(aLatestLog).getTime() : 0;
        bValue = bLatestLog ? new Date(bLatestLog).getTime() : 0;
        break;
      case 'lastActivity':
        aValue = getAdminLatestActivity(a) || '';
        bValue = getAdminLatestActivity(b) || '';
        break;
      default:
        break;
    }

    // normalize for comparison
    if (typeof aValue === 'string') aValue = aValue.toLowerCase();
    if (typeof bValue === 'string') bValue = bValue.toLowerCase();

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });
};

const UsersView: React.FC = () => {
  // ACTIONS AND SELECTORS
  const { getAdmins, deleteAdmin, resetError, resetAction } = useActions({
    getAdmins: adminsActions.getAdmins,
    deleteAdmin: adminsActions.deleteAdmin,
    resetError: adminsActions.resetError,
    resetAction: adminsActions.resetAction
  });
  const client = useSelector(clientsSelectors.selectClient) as Client;
  const admins = useSelector(adminsSelectors.selectAdmins);
  const adminData = useSelector(authSelectors.selectAdmin);
  const { adminId } = jwtDecode<{ [x: string]: string | number }>(useSelector(authSelectors.selectToken) as string);
  const { pending: adminsPending, successful, error } = useSelector(adminsSelectors.selectAction);
  const [readOnlyAdmin, setReadOnlyAdmin] = useState(true);

  // STATE OF TOGGLE CREATE USER OVERLAY | UPDATE USER DRAWER
  const [showCreateNewUser, setShowCreateNewUser] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);
  const [deleteParams, setDeleteParams] = useState<null | AdminWithActivity>(null);
  const [sortParams, setSortParams] = useState<{ key: string; order: 'asc' | 'desc' }>({
    key: 'type',
    order: 'asc'
  });

  const classes = useStyles({ showDrawer })();

  // STATE OF SELECT ADMIN
  const [selectedAdmin, setSelectedAdmin] = useState<AdminWithActivity | null>(null);

  // SORTS THE ADMINS
  const sortedAdmins = useMemo(() => {
    return sortAdmins(admins, sortParams.key, sortParams.order);
  }, [admins, sortParams]);

  // Setting loadMore to show scrollbar on table
  const loadMore = () => {};

  // ON UPDATE OF CLIENT || SUCCESSFUL
  React.useEffect(() => {
    // IF CLIENT EXIST OR SUCCESSFUL ACTION - GET ADMINS
    if (client || successful) getAdmins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, successful]);

  // ON SUCCESSFUL DELETION - RESET ACTION & DELETE PARAMS
  React.useEffect(() => {
    if (successful && deleteParams) {
      resetAction();
      setDeleteParams(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successful]);

  // check for user is Read-Only Admin or not, if yes hide Edit column and Add user button
  React.useEffect(() => {
    setReadOnlyAdmin(Boolean(adminData && adminData.role === 'ReadOnlyAdmin'));
  }, [adminData]);

  // ***************
  // ** FUNCTIONS **
  // ***************

  // EDIT ICON CLICK
  const onEditClick = (admin: AdminWithActivity) => {
    // SETS THE SELECTED ADMIN
    setSelectedAdmin(admin);

    // SETS THE DRAWER TO SHOW
    setShowDrawer(true);
  };

  // ON DRAWER CLOSE
  const onDrawerClose = () => {
    // SETS THE DRAWER TO HIDE
    setShowDrawer(false);

    // RESET ERROR
    if (error) resetError();

    // SETS THE SELECTED ADMIN TO BE NULL
    setSelectedAdmin(null);
  };

  const handleHeaderClick = (key: string) => {
    setSortParams((prev) => ({
      key,
      order: prev.key === key && prev.order === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortArrow = (sortArrowProps: { type: string }) => {
    return (
      <Box sx={{ display: 'inline-block', cursor: 'pointer' }} onClick={() => handleHeaderClick(sortArrowProps.type)}>
        <SvgIcon name={sortParams.order === 'asc' ? 'drop_up' : 'drop_down'} size={15} style={{ marginLeft: '5px' }} />
      </Box>
    );
  };
  return (
    <>
      {deleteParams && (
        <ActionOverlay
          title={
            <Box margin="0 0 5" width={400} display="contents">
              <Typography fontSize={40} fontWeight={600} align="center" sx={{ lineHeight: 1, margin: '0 0 24px' }}>
                Are You Sure?
              </Typography>
            </Box>
          }
          description={
            <Box width={500}>
              <Typography fontSize={24} align="center">
                Are you sure you want to delete this user?
              </Typography>
            </Box>
          }
          confirmText="Yes"
          appearance="error"
          onConfirm={() => deleteAdmin({ id: deleteParams.id })}
          cancelText="Cancel"
          onCancel={() => setDeleteParams(null)}
          height={300}
        />
      )}

      {/* HEADER ROW */}
      <Grid className={classes.usersHeading}>
        <Box flexOverride="none">
          <Typography className={classes.usersText}>Users</Typography>
        </Box>
        {!readOnlyAdmin && (
          <Button
            customSize="small"
            width={175}
            isLoading={adminsPending ? true : false}
            onClick={() => setShowCreateNewUser(true)}
            className={classes.userAddNewButton}
          >
            Add User
          </Button>
        )}
      </Grid>

      {/* TABLE OF USERS */}
      <MuiBox className={classes.usersTableOuterBox}>
        <Card hasBorderRadius raised fullHeight>
          <Box flexOverride="1">
            <MuiTable.TableContainer
              onScroll={() => loadMore()}
              id="userViewTable"
              className={classes.usersTableContainer}
            >
              <MuiTable.Table stickyHeader sx={{ borderCollapse: 'collapse' }}>
                <MuiTable.TableHead>
                  <MuiTable.TableRow>
                    <MuiTable.TableCell
                      className={classes.usersHeadersUsertype}
                      onClick={() => handleHeaderClick('type')}
                    >
                      Type {sortParams.key === 'type' && <SortArrow type={'type'} />}
                    </MuiTable.TableCell>
                    <MuiTable.TableCell className={classes.usersTableHeaders} onClick={() => handleHeaderClick('name')}>
                      Name {sortParams.key === 'name' && <SortArrow type={'name'} />}
                    </MuiTable.TableCell>
                    <MuiTable.TableCell
                      className={classes.usersTableHeaders}
                      onClick={() => handleHeaderClick('lastLogged')}
                    >
                      Last Logged {sortParams.key === 'lastLogged' && <SortArrow type={'lastLogged'} />}
                    </MuiTable.TableCell>
                    <MuiTable.TableCell
                      className={classes.usersTableHeaders}
                      onClick={() => handleHeaderClick('lastActivity')}
                    >
                      Last Activity {sortParams.key === 'lastActivity' && <SortArrow type={'lastActivity'} />}
                    </MuiTable.TableCell>
                    {!readOnlyAdmin && (
                      <MuiTable.TableCell
                        className={classes.usersTableHeaders}
                        align="right"
                        sx={{ marginRight: '10px', textAlign: 'center', width: '115px' }}
                      >
                        Actions
                      </MuiTable.TableCell>
                    )}
                  </MuiTable.TableRow>
                </MuiTable.TableHead>
                <MuiTable.TableBody>
                  {sortedAdmins?.map((usersObj: AdminWithActivity) => {
                    return (
                      <MuiTable.TableRow
                        className={classes.usersTableRows}
                        selected={
                          selectedAdmin && Object.keys(selectedAdmin).length
                            ? (selectedAdmin as AdminWithActivity).id === usersObj.id
                            : false
                        }
                        classes={{ selected: classes.selected }}
                      >
                        <MuiTable.TableCell className={classes.userRoleRow}>
                          <Typography fontSize={16} fontWeight={600}>
                            {formatTitle(getAdminRole(usersObj.role))}
                          </Typography>
                        </MuiTable.TableCell>
                        <MuiTable.TableCell>
                          <Typography fontSize={16}>{usersObj.name}</Typography>
                        </MuiTable.TableCell>
                        <MuiTable.TableCell>
                          <Typography fontSize={16}>{getAdminLatestLog(usersObj) || '--'}</Typography>
                        </MuiTable.TableCell>
                        <MuiTable.TableCell>
                          <Typography fontSize={16}>{getAdminLatestActivity(usersObj) || '--'}</Typography>
                        </MuiTable.TableCell>
                        {!readOnlyAdmin && (
                          <MuiTable.TableCell>
                            <FlexBox justify="between" sx={{ alignItems: 'center', gap: '10px' }}>
                              <SvgIconButton onClick={() => onEditClick(usersObj)} data-testid="update-user-button">
                                <SvgIcon name="edit_circle" />
                              </SvgIconButton>
                              {adminId !== usersObj.id && (
                                <SvgIconButton
                                  onClick={() => setDeleteParams(usersObj)}
                                  data-testid="delete-user-button"
                                >
                                  <SvgIcon name="exit_circle" />
                                </SvgIconButton>
                              )}
                            </FlexBox>
                          </MuiTable.TableCell>
                        )}
                      </MuiTable.TableRow>
                    );
                  })}
                </MuiTable.TableBody>
              </MuiTable.Table>
            </MuiTable.TableContainer>
          </Box>
        </Card>
      </MuiBox>
      <Grid className={classes.footer}>
        <p className={classes.total}>Users: {sortedAdmins.length}</p>
      </Grid>

      {/* UPDATEUSERDRAWER COMPONENT */}
      {selectedAdmin && (
        <UpdateUserDrawer onClose={onDrawerClose} isOpen={showDrawer} admin={selectedAdmin as AdminWithActivity} />
      )}

      {/* CREATENEWUSEROVERLAY COMPONENT */}
      {showCreateNewUser && (
        <CreateNewUserOverlay
          onConfirm={() => setShowCreateNewUser(false)}
          onCancel={() => setShowCreateNewUser(false)}
        />
      )}
    </>
  );
};

export default UsersView;
